/**
 * The CoinGecko fields of a terms object — loan or retainer project. The backend derives
 * `coinGeckoLink` from `coinGeckoProject`, so the link is only ever as fresh as the last
 * `Terms` frame.
 */
export interface CoinGeckoTerms {
  coinGeckoProject?: string;
  coinGeckoLink?: string;
}

/**
 * Whether `next` carries a CoinGecko project id other than the one in `previous`.
 *
 * An `undefined` id in `next` means "not touched" (a partial update), never "cleared" — a
 * cleared project is an empty string, which is also what a project stored before the id
 * existed counts as.
 */
export const isCoinGeckoProjectChanged = (
  previous: CoinGeckoTerms | undefined,
  next: CoinGeckoTerms | undefined,
): boolean =>
  next?.coinGeckoProject !== undefined &&
  next.coinGeckoProject !== (previous?.coinGeckoProject ?? '');

/**
 * What an optimistic terms merge has to add on top of the update it applies: once the
 * project changes, the stored link points at the old project, so it is dropped until the
 * server re-sends `Terms` rather than left linking to the wrong coin.
 */
export const staleCoinGeckoLinkPatch = (
  details: CoinGeckoTerms,
  update: CoinGeckoTerms,
): { coinGeckoLink?: '' } =>
  isCoinGeckoProjectChanged(details, update) ? { coinGeckoLink: '' } : {};

/**
 * Whether the update in flight changed the CoinGecko project, i.e. {@link staleCoinGeckoLinkPatch}
 * dropped the link and no `Terms` frame has brought the new one back yet.
 *
 * `confirmedTerms` is the server state from before that update; a `Terms` frame clears it, so
 * this is `false` as soon as the server's own link has arrived.
 */
export const isCoinGeckoLinkAwaitingTerms = ({
  confirmedTerms,
  details,
}: {
  confirmedTerms?: { details?: CoinGeckoTerms };
  details?: CoinGeckoTerms;
}): boolean =>
  confirmedTerms?.details !== undefined &&
  isCoinGeckoProjectChanged(confirmedTerms.details, details);
